import React, { useEffect, useState } from 'react';
import {
  ChevronLeft, ChevronRight, History, Loader2, TrendingUp, TrendingDown,
} from 'lucide-react';
import client from '../../lib/api';
import { accountHook } from '../../lib/domHooks';

const money = (v) => `${Number(v || 0).toFixed(2)} ₺`;

const relative = (d) => {
  if (!d) return '-';
  const diff = Date.now() - new Date(d).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Az önce';
  if (min < 60) return `${min} dakika önce`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} saat önce`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'Dün';
  if (day < 7) return `${day} gün önce`;
  return new Date(d).toLocaleDateString('tr-TR');
};

// Sayfa başına hareket satırı (bir tur genelde bahis + kazanç = iki satır).
// Ekran bir süre tek istekte 200 satır alıp hepsini birden çiziyordu.
export const GAME_HISTORY_PAGE_SIZE = 50;

const GameHistory = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    client.get('/games/rounds', {
      params: { limit: GAME_HISTORY_PAGE_SIZE, skip: page * GAME_HISTORY_PAGE_SIZE },
    })
      .then(({ data }) => {
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(Number(data.total || 0));
        setHasMore(Boolean(data.has_more));
      })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page]);

  const pageCount = Math.max(1, Math.ceil(total / GAME_HISTORY_PAGE_SIZE));
  // Sayfa değişince liste başına dönülür; oyuncu yeni sayfanın ortasından
  // okumaya başlamamalıdır.
  const goToPage = (next) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Aynı tur (roundId) içindeki bahis ve kazanç kayıtlarını birleştir
  const rounds = React.useMemo(() => {
    const map = new Map();
    for (const r of items) {
      const key = r.round_id || r.trans_id;
      const cur = map.get(key) || {
        key, game_name: r.game_name, provider_name: r.provider_name,
        bet: 0, win: 0, created_at: r.created_at,
      };
      cur.bet += Number(r.bet || 0);
      cur.win += Number(r.win || 0);
      if (new Date(r.created_at) < new Date(cur.created_at)) cur.created_at = r.created_at;
      map.set(key, cur);
    }
    return Array.from(map.values()).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [items]);

  const totals = rounds.reduce(
    (a, r) => ({ bet: a.bet + r.bet, win: a.win + r.win }),
    { bet: 0, win: 0 },
  );

  if (loading && page === 0 && !items.length) {
    return (
      <div className="pusu-panel p-10 text-center text-gray-400" {...accountHook('loading')}>
        <Loader2 className="inline animate-spin mr-2 text-[var(--pusu-gold)]" /> Yükleniyor...
      </div>
    );
  }

  return (
    <div className="pusu-panel p-6" {...accountHook('panel')}>
      <div className="flex items-center gap-3 mb-5" {...accountHook('page-header')}>
        <History size={20} className="text-[var(--pusu-gold)]" />
        <h2 className="text-xl font-bold gold-text" {...accountHook('title')}>Oyun Geçmişi</h2>
        {rounds.length > 0 && (
          // Toplamlar yalnızca görüntülenen sayfanındır; etiket bunu söyler,
          // yoksa oyuncu tek sayfanın rakamını bütün geçmişi sanırdı.
          <div className="ml-auto flex gap-2 text-xs" {...accountHook('summary')}>
            <span className="badge-pill badge-gray">Sayfa bahis: {money(totals.bet)}</span>
            <span className={`badge-pill ${totals.win >= totals.bet ? 'badge-green' : 'badge-red'}`}>
              Sayfa kazanç: {money(totals.win)}
            </span>
          </div>
        )}
      </div>

      {rounds.length === 0 && page === 0 ? (
        <div className="pusu-card p-8 text-center text-gray-500" {...accountHook('empty')}>
          Henüz oyun kaydınız yok. Casino oyunlarını oynadıkça turlarınız burada listelenir.
        </div>
      ) : (
        <div className="space-y-2" {...accountHook('list')}>
          {rounds.map((h) => {
            const net = h.win - h.bet;
            return (
              <div
                key={h.key}
                className="pusu-card p-3 flex items-center gap-3"
                data-bhs-status={net >= 0 ? 'win' : 'loss'}
                {...accountHook('row')}
              >
                <div className={`${net >= 0 ? 'account-result-win' : 'account-result-loss'} w-10 h-10 rounded-lg flex items-center justify-center shrink-0`}>
                  {net >= 0 ? <TrendingUp size={16} className="text-green-400" /> : <TrendingDown size={16} className="text-red-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{h.game_name || 'Oyun'}</div>
                  <div className="text-xs text-gray-400 truncate">
                    {relative(h.created_at)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-gray-400">Bahis</div>
                  <div className="text-sm font-bold">{money(h.bet)}</div>
                </div>
                <div className="text-right min-w-[90px]">
                  <div className="text-[11px] text-gray-400">Kazanç</div>
                  <div className={`text-sm font-bold ${h.win > h.bet ? 'text-green-400' : h.win === 0 ? 'text-red-400' : 'text-gray-300'}`}>
                    {money(h.win)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {(page > 0 || hasMore) && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5" {...accountHook('pagination')}>
          <button
            type="button"
            disabled={page === 0 || loading}
            onClick={() => goToPage(Math.max(0, page - 1))}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft size={14} /> Önceki
          </button>
          <span className="text-xs font-bold text-gray-400">
            {loading
              ? <Loader2 size={14} className="inline animate-spin text-[var(--pusu-gold)]" />
              : `Sayfa ${page + 1} / ${pageCount}`}
          </span>
          <button
            type="button"
            disabled={!hasMore || loading}
            onClick={() => goToPage(page + 1)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Sonraki <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default GameHistory;
